import React, { useState, useRef, useCallback, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";
import { useOAuthPopupConnect } from "../../hooks/oauthPopup";
import { DropdownSingle, colors2, UI_BODY_SM_DF, DateRangePicker, DatePicker } from "oolib";
import { ButtonPrimary, ButtonSecondary } from "oolib/dist/v2/components/Buttons";
import {
  InterfaceContainer,
  InterfaceHeader,
  InterfaceDescription,
  InterfaceActions,
  HelpText,
  ConnectedBanner,
  ConnectedMeta,
  StaleBanner,
  ErrorBanner,
  EmptyBanner,
} from "./BaseIntegrationInterface";
import { IntegrationDisconnectConfirmModal } from "@/_self_managed/SM_Integrations/IntegrationDisconnectConfirmModal";

// Intercom does NOT use the useOAuthConnection hook — its connected state
// drives an async batch job (initiate → poll → complete) with a progress bar
// and the existing handlers already own equivalent connection state inline.
// We adopt the shared banner styled components above for visual parity.

// ============================================
// INTERCOM ICON
// ============================================

export const IntercomIcon = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="4" fill="#286EFA" />
    <path
      d="M19.5 15.5c0 0-2.5 2.5-7.5 2.5s-7.5-2.5-7.5-2.5"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      fill="none"
    />
    <line x1="6" y1="7" x2="6" y2="13" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="9" y1="5.5" x2="9" y2="13.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="12" y1="5" x2="12" y2="14" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="15" y1="5.5" x2="15" y2="13.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="18" y1="7" x2="18" y2="13" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// ============================================
// CONSTANTS
// ============================================

const STATE_OPTIONS = [
  { display: "All", value: "all" },
  { display: "Open", value: "open" },
  { display: "Closed", value: "closed" },
  { display: "Snoozed", value: "snoozed" },
];

const BATCH_STATUS_LABELS = {
  initiating: "Starting fetch job...",
  Pending: "Waiting in queue...",
  Running: "Fetching conversations...",
  completing: "Preparing preview...",
};

const POLL_INTERVAL_MS = 3000;
const DAY_MS = 24 * 60 * 60 * 1000;
const SOFT_WARN_DAYS = 90;
const HARD_CAP_DAYS = 365;

const isoDaysAgo = (n) => new Date(Date.now() - n * DAY_MS).toISOString().slice(0, 10);
const todayIso = () => new Date().toISOString().slice(0, 10);

// ============================================
// STYLED COMPONENTS
// ============================================

const FieldGroup = styled.div`
  margin-top: 16px;
`;

const DateRangeRow = styled.div`
  margin-top: 16px;
`;

const RangeHint = styled(UI_BODY_SM_DF)`
  color: ${({ $error }) => ($error ? "#c0392b" : colors2.grey60)};
  margin-top: 8px;
`;

const ProgressContainer = styled.div`
  margin-top: 24px;
  text-align: center;
`;

const ProgressBarOuter = styled.div`
  width: 100%;
  height: 8px;
  background: ${({ theme }) => theme?.colors?.lightGrey || "#e0e0e0"};
  border-radius: 4px;
  margin-top: 12px;
  overflow: hidden;
`;

const ProgressBarInner = styled.div`
  height: 100%;
  background: ${({ theme }) => theme?.colors?.primaryColor || "#286EFA"};
  border-radius: 4px;
  transition: width 0.5s ease;
  width: ${({ progress }) => progress}%;
`;

const StatusText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme?.colors?.greyColor60 || "#666"};
  margin: 8px 0 0;
`;

const ProgressPercent = styled.p`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
`;

// ============================================
// INTERCOM INTERFACE — OAuth-based persistent connection
// ============================================

export const IntercomInterface = ({ onSubmit, isLoading }) => {
  // Connection state: null while loading, then { connected, status, providerAccountName, adminEmail }
  const [connection, setConnection] = useState(null);
  const [connectionLoading, setConnectionLoading] = useState(true);
  const [connectError, setConnectError] = useState(null);

  // Import filters
  const [dateRange, setDateRange] = useState([isoDaysAgo(30), todayIso()]);
  const [startDate, endDate] = dateRange;
  const [convState, setConvState] = useState(STATE_OPTIONS[0]);

  // Batch state (import run)
  const [batchState, setBatchState] = useState(null); // null | "initiating" | "polling" | "completing" | "empty" | "error"
  const [batchJobId, setBatchJobId] = useState(null);
  const [batchProgress, setBatchProgress] = useState(0);
  const [batchStatusLabel, setBatchStatusLabel] = useState("");
  const [batchError, setBatchError] = useState(null);
  const pollIntervalRef = useRef(null);

  // Date range validation
  const rangeDays = startDate && endDate
    ? Math.round((new Date(endDate) - new Date(startDate)) / DAY_MS)
    : 0;
  const rangeTooLong = rangeDays > HARD_CAP_DAYS;
  const rangeWarn = !rangeTooLong && rangeDays > SOFT_WARN_DAYS;
  const rangeInverted = startDate && endDate && new Date(startDate) > new Date(endDate);

  const isBatchInProgress = batchState && batchState !== "error" && batchState !== "empty";
  const canFetch = connection?.connected
    && connection?.status !== "stale"
    && startDate && endDate && !rangeTooLong && !rangeInverted;

  const fetchConnectionStatus = useCallback(async () => {
    setConnectionLoading(true);
    try {
      const { data } = await axios.get("/api/externalConnections/intercom/status");
      setConnection(data);
    } catch (err) {
      setConnectError(err.response?.data?.error || err.message || "Failed to read connection status");
    } finally {
      setConnectionLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnectionStatus();
  }, [fetchConnectionStatus]);

  // Handle return from OAuth redirect — refetch status, show error if provider signaled one
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("externalConnectionResult");
    const provider = params.get("provider");
    if (result && provider === "intercom") {
      if (result === "error") {
        const reason = params.get("reason") || "unknown";
        setConnectError(`Intercom connection failed: ${reason}. Please try again.`);
      }
      fetchConnectionStatus();
      // Clean the URL so refreshing the page doesn't re-trigger
      params.delete("externalConnectionResult");
      params.delete("provider");
      params.delete("reason");
      const qs = params.toString();
      const newUrl = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }
  }, [fetchConnectionStatus]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  // Popup OAuth: the shared hook opens the popup, points it at the provider, and
  // calls onResult when the popup reports back (full-page redirect fallback is
  // handled by the URL-param effect above).
  const { startConnect, isConnecting } = useOAuthPopupConnect({
    providerId: "intercom",
    providerName: "Intercom",
    onResult: ({ result, reason }) => {
      if (result === "error") {
        setConnectError(`Intercom connection failed: ${reason || "unknown"}. Please try again.`);
      }
      fetchConnectionStatus();
    },
  });

  const handleConnect = useCallback(async () => {
    setConnectError(null);
    try {
      await startConnect();
    } catch (err) {
      setConnectError(err.response?.data?.error || err.message || "Could not start Intercom connection");
    }
  }, [startConnect]);

  const handleDisconnect = useCallback(async () => {
    setConnectError(null);
    try {
      await axios.post("/api/externalConnections/intercom/disconnect");
      setConnection({ connected: false });
    } catch (err) {
      setConnectError(err.response?.data?.error || err.message || "Disconnect failed");
    }
  }, []);

  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const confirmDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await handleDisconnect();
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectConfirm(false);
    }
  };

  const startPolling = useCallback((jobId) => {
    const poll = async () => {
      try {
        const { data } = await axios.post("/api/externalImport/batch/status", {
          source: { id: "intercom" },
          jobId,
        });

        setBatchProgress(data.progress || 0);
        const label = BATCH_STATUS_LABELS[data.status] || data.status;
        const detail = data.fetched
          ? ` (Fetched ${data.fetched}${data.total ? ` of ~${data.total}` : ""} conversations)`
          : "";
        setBatchStatusLabel(`${label}${detail}`);

        if (data.status === "Completed") {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;

          // Worker reports fetched=conversations.length when phase=completed.
          // Treat 0 conversations as a friendly empty state, not a failure —
          // skip the GCS/preview pipeline entirely.
          if (data.fetched === 0) {
            setBatchState("empty");
            setBatchProgress(100);
            setBatchStatusLabel("");
            return;
          }

          setBatchState("completing");
          setBatchStatusLabel(BATCH_STATUS_LABELS.completing);
          setBatchProgress(100);

          onSubmit({
            // No apiKey — BE resolves from the stored connection
            adapterOptions: {
              batchJobId: jobId,
              startDate,
              endDate,
              state: convState?.value || "all",
            },
          });
        } else if (data.status === "Failed") {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setBatchState("error");
          setBatchError(data.errorMessage || "Intercom fetch job failed. Please try again.");
          // A 401 during fetch will have marked the connection stale server-side.
          // Refresh status so the UI flips to the Reconnect banner.
          fetchConnectionStatus();
        }
      } catch (err) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setBatchState("error");
        setBatchError(err.response?.data?.error || err.message || "Failed to check job status");
      }
    };

    poll();
    pollIntervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
  }, [startDate, endDate, convState, onSubmit, fetchConnectionStatus]);

  const handleFetch = useCallback(async () => {
    if (!canFetch || isBatchInProgress) return;

    setBatchState("initiating");
    setBatchStatusLabel(BATCH_STATUS_LABELS.initiating);
    setBatchProgress(0);
    setBatchError(null);

    try {
      const { data } = await axios.post("/api/externalImport/batch/initiate", {
        source: {
          id: "intercom",
          options: { startDate, endDate, state: convState?.value || "all" },
        },
      });

      if (!data.batchJobId) throw new Error("No batchJobId returned from server");

      setBatchJobId(data.batchJobId);
      setBatchState("polling");
      startPolling(data.batchJobId);
    } catch (err) {
      setBatchState("error");
      const msg = err.response?.data?.error || err.message || "Failed to start Intercom fetch job";
      setBatchError(msg);
      // If the server reports no/stale connection, flip the UI accordingly.
      if (/reconnect|connection/i.test(msg)) {
        fetchConnectionStatus();
      }
    }
  }, [canFetch, isBatchInProgress, startDate, endDate, convState, startPolling, fetchConnectionStatus]);

  const handleRetry = useCallback(() => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    setBatchState(null);
    setBatchJobId(null);
    setBatchProgress(0);
    setBatchStatusLabel("");
    setBatchError(null);
  }, []);

  // ------- Render -------

  if (connectionLoading) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Intercom</InterfaceHeader>
        <InterfaceDescription>Checking connection status…</InterfaceDescription>
      </InterfaceContainer>
    );
  }

  // State 1: not connected
  if (!connection?.connected) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Intercom</InterfaceHeader>
        <InterfaceDescription>
          Import support conversations from Intercom. You'll be redirected to
          Intercom to authorize access, then brought back here to pick a date
          range and run the import.
        </InterfaceDescription>

        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}

        <InterfaceActions>
          <ButtonPrimary onClick={handleConnect} isLoading={isConnecting} disabled={isConnecting}>Connect Intercom</ButtonPrimary>
        </InterfaceActions>

        <HelpText>
          Dots uses OAuth to access your Intercom workspace. We only request
          read access to conversations, users, tags, and admins.
        </HelpText>
      </InterfaceContainer>
    );
  }

  // State 3: connection exists but is stale
  if (connection.status === "stale") {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Reconnect Intercom</InterfaceHeader>
        <StaleBanner>
          <ConnectedMeta>
            <strong>⚠ Connection needs to be renewed</strong>
            <span>
              {connection.providerAccountName
                ? `Your Intercom connection to ${connection.providerAccountName} expired or was revoked.`
                : "Your Intercom connection expired or was revoked."}
            </span>
          </ConnectedMeta>
          <ButtonPrimary onClick={handleConnect} isLoading={isConnecting} disabled={isConnecting}>Reconnect Intercom</ButtonPrimary>
        </StaleBanner>
        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
      </InterfaceContainer>
    );
  }

  // State 2: connected + ready to fetch
  return (
    <InterfaceContainer>
      <InterfaceHeader>Import from Intercom</InterfaceHeader>
      <InterfaceDescription>
        Each conversation becomes one row. Full thread (including internal
        notes) is formatted as a transcript.
      </InterfaceDescription>

      <ConnectedBanner>
        <ConnectedMeta>
          <strong>
            ✓ Connected{connection.providerAccountName ? ` as ${connection.providerAccountName}` : ""}
          </strong>
          {connection.adminEmail && <span style={{ color: colors2.grey60 }}>{connection.adminEmail}</span>}
        </ConnectedMeta>
        <ButtonSecondary onClick={() => setShowDisconnectConfirm(true)} disabled={isBatchInProgress}>
          Disconnect
        </ButtonSecondary>
      </ConnectedBanner>

      {showDisconnectConfirm && (
        <IntegrationDisconnectConfirmModal
          name="Intercom"
          onConfirm={confirmDisconnect}
          onClose={() => setShowDisconnectConfirm(false)}
          isLoading={isDisconnecting}
        />
      )}

      {connectError && <ErrorBanner>{connectError}</ErrorBanner>}

      <DateRangeRow>
        <DateRangePicker
          id="intercom-date-range"
          label="Date Range"
          value={dateRange}
          onChange={(k, v) => setDateRange(v)}
          readOnly={isBatchInProgress}
        />
      </DateRangeRow>

      {rangeInverted && (
        <RangeHint $error>End date must be on or after start date.</RangeHint>
      )}
      {rangeTooLong && (
        <RangeHint $error>
          Please select a range of {HARD_CAP_DAYS} days or fewer. Run multiple imports for longer history.
        </RangeHint>
      )}
      {rangeWarn && (
        <RangeHint>
          Large date ranges ({rangeDays} days) may take several minutes to import.
        </RangeHint>
      )}

      <FieldGroup>
        <DropdownSingle
          label="Conversation State"
          placeholder="Select state..."
          options={STATE_OPTIONS}
          value={convState}
          onChange={(k, v) => setConvState(v)}
          readOnly={isBatchInProgress}
          lightboxHeight="small"
          popOutOfOverflowHiddenParent={true}
        />
      </FieldGroup>

      {isBatchInProgress && (
        <ProgressContainer>
          <ProgressPercent>{batchProgress}%</ProgressPercent>
          <ProgressBarOuter>
            <ProgressBarInner progress={batchProgress} />
          </ProgressBarOuter>
          <StatusText>{batchStatusLabel}</StatusText>
        </ProgressContainer>
      )}

      {batchState === "error" && <ErrorBanner>{batchError}</ErrorBanner>}

      {batchState === "empty" && (
        <EmptyBanner>
          No conversations found in Intercom for {startDate} → {endDate}
          {convState?.value && convState.value !== "all" ? ` (state: ${convState.value})` : ""}.
          Try changing the date range.
        </EmptyBanner>
      )}

      <InterfaceActions>
        {batchState === "error" || batchState === "empty" ? (
          <ButtonPrimary onClick={handleRetry}>Try Again</ButtonPrimary>
        ) : (
          <ButtonPrimary
            onClick={handleFetch}
            disabled={!canFetch || isBatchInProgress || isLoading}
            isLoading={isBatchInProgress || isLoading}
          >
            Fetch Conversations
          </ButtonPrimary>
        )}
      </InterfaceActions>
    </InterfaceContainer>
  );
};

// ============================================
// INTEGRATION META
// ============================================

export const intercomIntegrationMeta = {
  id: "intercom",
  name: "Intercom",
  description: "Import support conversations from Intercom",
};

export default IntercomInterface;
